import CardLoading from '@op/components/CardLoading';
import ContentBody from '@op/components/ContentBody';
import { FilterFixed, useFilterDataHook } from '@op/components/FilterCommon';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Layout, Segment } from 'tea-component';
import { connect } from 'umi';
import TrendingVideo from './TrendingVideo';
import useLanguage from '@op/hooks/useLanguage';
import { languageSelectOptions } from '@op/components/FilterBoxCommon/constant';
import TranslateSelect from '@op/components/SelectComponents/TranslateSelect';
import { LanguageType } from '@op/types/common';
import TrendingPosts from './TrendingPosts';
import './index.scss';
import { distinguishOfficialAccounts } from '@op/components/FilterCommon/utils/monitorUtil';
import {
  buriedBtnPoint,
  useInitBuriedPointInfo,
} from '@op/operationLog/buriedPoint';
import trendingPublications from '@op/operationLog/pages/trendingPublications';
import { PAGE_NAME, getViaDetailFlagKey } from '@op/constants/page';
import { DBO_TM } from '@op/utils/format';
import usePageStateCache from '@op/hooks/usePageStateCache';

const { Content } = Layout;
type ValueType = 'official-accounts' | 'others' | 'total';
type PageState = {
  scrollTop: number;
  value: ValueType;
  language: LanguageType;
  videoState: {
    sortBy: { by: string; order: string };
    isActive: boolean;
    view: string;
  };
  postsState: {
    sortBy: { by: string; order: string };
    isActive: boolean;
    view: string;
  };
};

const DEFAULT_VIDEO_STATE = {
  sortBy: { by: 'engagement', order: 'desc' },
  isActive: false,
  view: 'card',
};

const DEFAULT_POSTS_STATE = {
  sortBy: { by: 'engagement', order: 'desc' },
  isActive: false,
  view: 'card',
};

function TrendingPublications({ gameInfo, wholeGameInfo, trendingVideoLoaded, trendingPostsLoaded, dispatch }: any) {
  const { formatMessage, formatOptionsMessage } = useLanguage();
  const [language, setLanguage] = useState<LanguageType>('origin');
  const [value, setValue] = useState<ValueType>('total');
  const accountCondRef = useRef<{ [key in ValueType]?: Array<string> }>({});

  // 用于追踪"当前值"的 ref，避免 useEffect cleanup 中的闭包过期问题
  const valueRef = useRef<ValueType>(value);
  const languageRef = useRef<LanguageType>(language);
  valueRef.current = value;
  languageRef.current = language;
  // 检查是否从详情页返回（按 gameId 区分），只有带标记才恢复缓存状态
  const shouldRestore = useMemo(() => {
    const gameId = gameInfo?.edition_unified_id;
    if (!gameId) return false;
    const key = getViaDetailFlagKey(gameId);
    const flag = sessionStorage.getItem(key);
    if (flag) {
      sessionStorage.removeItem(key);
      return true;
    }
    return false;
  }, [gameInfo?.edition_unified_id]);

  // 子组件状态 ref，子组件会自行同步
  const videoStateRef = useRef<PageState['videoState']>({ ...DEFAULT_VIDEO_STATE });
  const postsStateRef = useRef<PageState['postsState']>({ ...DEFAULT_POSTS_STATE });

  const {
    scrollBody,
    loading,
    filter,
    changeFilter,
    filterOptions,
    userSynchronousValue,
    changeUserSynchronousValue,
    confirmRef,
  } = useFilterDataHook({
    gameInfo,
    pageName: 'Trending_Publications',
    defaultUserSynchronousValue: {
      social_media_by_account: ['99999'],
      social_media_by_keyword: ['99999'],
      external_data_sources: ['99999'],
    },
  });



  // 页面状态缓存：仅从详情页返回时恢复，卸载时保存状态
  const { restoreScroll } = usePageStateCache({
    pageName: 'trendingPublications',
    gameId: gameInfo?.edition_unified_id,
    shouldRestore,
    getState: () => ({
      scrollTop: scrollBody?.current?.scrollTop || 0,
      value: valueRef.current,
      language: languageRef.current,
      videoState: videoStateRef.current,
      postsState: postsStateRef.current,
    }),
    onRestore: (saved) => {
      setValue(saved.value || 'total');
      setLanguage(saved.language || 'origin');
      videoStateRef.current = saved.videoState || { ...DEFAULT_VIDEO_STATE };
      postsStateRef.current = saved.postsState || { ...DEFAULT_POSTS_STATE };
    },
    scrollBodyRef: scrollBody,
  });

  // 两个子组件接口请求均完成后，恢复滚动位置
  useEffect(() => { 
    if (trendingVideoLoaded && trendingPostsLoaded) {
      restoreScroll();
    }
  }, [trendingVideoLoaded, trendingPostsLoaded, restoreScroll]);


  // 挂载时重置为 0，避免上次离开页面残留的状态
  useEffect(() => {
    return () => {
      dispatch({
        type: 'opinionOrderLoading/resetLoading',
        keys: ["trendingVideo", "trendingPosts"],
        defaultValue: 0
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useInitBuriedPointInfo({ buriedPoint: trendingPublications });
  const params = useMemo(() => {
    const obj = { ...filter };

    // 当为其他官号时，设置一个排除官方账号, 其他页面区分直接根据tab进行区分就好了
    if (value === 'others') {
      obj.except_social_media_by_account =
        accountCondRef.current['official-accounts'] || [];
      obj.organization = 'player';
    }
    if (value === 'official-accounts') {
      obj.social_media_by_keyword = ['-99999'];
      obj.social_media_by_account = obj?.social_media_by_account?.filter(
        (item: string) =>
          accountCondRef.current['official-accounts']?.includes(item),
      );
      obj.organization = 'official';
    }
    return obj;
  }, [filter]);

  return (
    <Content className="dbo-page-publications" ref={scrollBody}>
      <ContentBody
        contentHeard={
          <Content.Header
            className="dbo-header"
            title={formatMessage({ id: PAGE_NAME.TRENDING_PUBLICATIONS })}
          ></Content.Header>
        }
        pageName={PAGE_NAME.TRENDING_PUBLICATIONS}
      >
        <FilterFixed
          scrollBody={scrollBody}
          filter={filterOptions}
          confirmRef={confirmRef}
          userSynchronousValue={userSynchronousValue}
          defaultValue={DBO_TM.utcFilterRangeDay(6, 0)}
          defaultConfig={{
            channel: {
              channelKey: 'social',
            },
          }}
          backCondOriginData={(cond) => {
            const account = cond?.monitor_by_account_category?.monitory || {};
            accountCondRef.current = {
              ...distinguishOfficialAccounts(account),
              total: ['public'],
            };
          }}
          confirm={(value: any) => {
            changeFilter({ ...value, ...gameInfo });
          }}
        ></FilterFixed>

        <CardLoading loading={loading}>
          <div className="dbo-filter-translateSelect">
            <Segment
              className="segment-light"
              value={value.toString()}
              onChange={(value: ValueType) => {
                const obj: any = {
                  social_media_by_account: accountCondRef.current?.[value] || [
                    'public',
                  ],
                  social_media_by_keyword:
                    value === 'official-accounts' ? ['-99999'] : ['99999'],
                  external_data_sources:
                    value === 'total' ? ['99999'] : ['-99999'],
                  except_social_media_by_account: [],
                  except_social_media_by_keyword: [],
                  except_external_data_sources: [],
                };

                changeUserSynchronousValue(obj);
                setValue(value);
                buriedBtnPoint('TP_Tab', value);
              }}
              options={formatOptionsMessage([
                { text: 'Total', value: 'total' },
                { text: 'Official Account', value: 'official-accounts' },
                { text: 'Earned Content', value: 'others' },
              ])}
              style={{ marginRight: 10 }}
            />
            <TranslateSelect
              langOptions={formatOptionsMessage(languageSelectOptions)}
              lang={language}
              isSingle={true}
              getChange={(e: any) => {
                setLanguage(e);
                buriedBtnPoint('TP_Translate', e);
              }}
            />
          </div>
          <TrendingVideo
            language={language}
            filter={params}
            wholeGameInfo={wholeGameInfo}
            initialSortBy={videoStateRef.current.sortBy}
            initialIsActive={videoStateRef.current.isActive}
            initialView={videoStateRef.current.view}
            videoStateOuterRef={videoStateRef}
          ></TrendingVideo>
          <TrendingPosts
            language={language}
            filter={params}
            wholeGameInfo={wholeGameInfo}
            initialSortBy={postsStateRef.current.sortBy}
            initialIsActive={postsStateRef.current.isActive}
            initialView={postsStateRef.current.view}
            postsStateOuterRef={postsStateRef}
          ></TrendingPosts>
        </CardLoading>
      </ContentBody>
    </Content>
  );
}

export default connect((state: any) => {
  const { opinionCommon, opinionFilter, opinionOrderLoading } = state;
  return {
    gameInfo: opinionCommon.gameInfo,
    filter: opinionFilter,
    wholeGameInfo: opinionCommon.wholeGameInfo,
    trendingVideoLoaded: opinionOrderLoading?.trendingVideo,
    trendingPostsLoaded: opinionOrderLoading?.trendingPosts,
  };
})(TrendingPublications);
